import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { getDocument } from '../utils/matchHelpers';
import Footer from '../components/Footer';

function getRoundLabel(roundNum) {
  if (roundNum === 2)  return 'Final';
  if (roundNum === 4)  return 'Semi Finals';
  if (roundNum === 8)  return 'Quarter Finals';
  if (roundNum === 16) return 'Round of 16';
  if (roundNum === 32) return 'Round of 32';
  return `Round of ${roundNum}`;
}

export default function TournamentResults() {
  const { id } = useParams();
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [tournament, setTournament] = useState(null);
  const [champion, setChampion]     = useState(null);
  const [runnerUp, setRunnerUp]     = useState(null);
  const [teams, setTeams]           = useState([]);
  const [matches, setMatches]       = useState([]);
  const [playerCount, setPlayerCount] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const tournDoc = await getDoc(doc(db, 'tournaments', id));
        if (!tournDoc.exists()) { setError('Tournament not found'); setLoading(false); return; }
        const tourn = { id: tournDoc.id, ...tournDoc.data() };
        setTournament(tourn);

        const [teamsSnap, matchesSnap] = await Promise.all([
          getDocs(query(collection(db, 'teams'), where('tournament_id', '==', id))),
          getDocs(query(collection(db, 'matches'), where('tournament_id', '==', id))),
        ]);

        const teamsData   = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const matchesData = matchesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const teamMap = {};
        teamsData.forEach(t => { teamMap[t.id] = t; });

        const enriched = matchesData.map(m => ({
          ...m,
          team1_name: teamMap[m.team1_id]?.name || null,
          team2_name: teamMap[m.team2_id]?.name || null,
        }));

        setTeams(teamsData);
        setMatches(enriched);

        const playerIds = new Set();
        teamsData.forEach(t => {
          if (t.player1_id) playerIds.add(t.player1_id);
          if (t.player2_id) playerIds.add(t.player2_id);
        });
        setPlayerCount(playerIds.size);

        if (tourn.champion_team_id) {
          const c = await getDocument('teams', tourn.champion_team_id);
          setChampion(c);
        }
        if (tourn.runner_up_team_id) {
          const r = await getDocument('teams', tourn.runner_up_team_id);
          setRunnerUp(r);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0f1923', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '15px' }}>
      Loading results…
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#0f1923', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e74c3c', fontSize: '15px' }}>
      {error}
    </div>
  );

  const completed     = matches.filter(m => m.status === 'completed');
  const highestScore  = completed.reduce((max, m) => Math.max(max, m.team1_score || 0, m.team2_score || 0), 0);

  const knockoutMatches = completed.filter(m => m.match_type === 'knockout');
  const groupMatches    = completed.filter(m => m.match_type !== 'knockout');

  // Final match (knockout_round_num === 2)
  const finalMatch = knockoutMatches.find(m => m.knockout_round_num === 2);

  // Non-final knockout matches grouped by round
  const otherKnockout = knockoutMatches.filter(m => m.knockout_round_num !== 2);
  const knockoutByRound = {};
  otherKnockout.forEach(m => {
    const rn = m.knockout_round_num || 4;
    if (!knockoutByRound[rn]) knockoutByRound[rn] = [];
    knockoutByRound[rn].push(m);
  });
  // Sort rounds ascending by roundNum so Final (2) would come first — but Final is separate
  // Semi (4), QF (8), R16 (16)... sort ascending = most recent stages first
  const knockoutRounds = Object.keys(knockoutByRound).map(Number).sort((a, b) => a - b);

  const stats = [
    { icon: '👥', label: 'Teams',          value: teams.length },
    { icon: '🎮', label: 'Matches Played', value: completed.length },
    { icon: '🧑', label: 'Total Players',  value: playerCount },
    { icon: '🔥', label: 'Highest Score',  value: highestScore || '—' },
    { icon: '📊', label: 'Format',         value: tournament?.format?.replace(/_/g, ' ') || '—' },
    { icon: '🏓', label: 'Match Type',     value: tournament?.type || '—' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg,#0f1923 0%,#1a2332 55%,#0d2b1f 100%)', color: '#fff', fontFamily: 'inherit' }}>

      {/* Nav */}
      <div style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '0 20px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', height: '58px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
            <span style={{ fontSize: '20px' }}>🏓</span>
            <span style={{ fontSize: '16px', fontWeight: '900', color: '#27ae60' }}>Live</span>
            <span style={{ fontSize: '16px', fontWeight: '900', color: '#fff' }}>PickleBall</span>
          </Link>
          <Link to="/" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>← Back to Home</Link>
        </div>
      </div>

      {/* Poster banner */}
      {tournament?.poster_url && (
        <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
          <img src={tournament.poster_url} alt={tournament.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.55 }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(15,25,35,0.3) 0%, #0f1923 100%)' }} />
        </div>
      )}

      <div style={{ maxWidth: '860px', margin: '0 auto', padding: `${tournament?.poster_url ? '0' : '32px'} 16px 60px` }}>

        {/* Tournament title */}
        <div style={{ textAlign: 'center', padding: '32px 20px 28px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#27ae60', textTransform: 'uppercase', letterSpacing: '2.5px', marginBottom: '10px' }}>
            Tournament Results
          </div>
          <h1 style={{ fontSize: 'clamp(22px, 5vw, 36px)', fontWeight: '900', margin: '0 0 16px', lineHeight: 1.1 }}>
            {tournament?.name}
          </h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
            {[
              tournament?.type,
              tournament?.format?.replace(/_/g, ' '),
              `To ${tournament?.points_to_win} pts`,
              tournament?.completed_at
                ? new Date(tournament.completed_at).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })
                : null,
            ].filter(Boolean).map((tag, i) => (
              <span key={i} style={{ fontSize: '12px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '4px 14px', color: 'rgba(255,255,255,0.55)' }}>
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* ── CHAMPION + RUNNER-UP ── */}
        {champion ? (
          <div style={{ marginBottom: '28px' }}>
            {/* Section label */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#f39c12', textTransform: 'uppercase', letterSpacing: '2px', background: 'rgba(243,156,18,0.1)', border: '1px solid rgba(243,156,18,0.25)', borderRadius: '20px', padding: '4px 16px' }}>
                🏆 Final Standings
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: runnerUp ? '1fr 1fr' : '1fr', gap: '12px' }}>
              {/* Champion card */}
              <div style={{ background: 'linear-gradient(135deg,#1a1200 0%,#2d1f00 40%,#1a1a2e 100%)', borderRadius: '18px', padding: '28px 20px', textAlign: 'center', position: 'relative', overflow: 'hidden', border: '2px solid rgba(243,156,18,0.5)', boxShadow: '0 0 40px rgba(243,156,18,0.15), inset 0 1px 0 rgba(255,255,255,0.06)' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 0%, rgba(243,156,18,0.25) 0%, transparent 65%)', pointerEvents: 'none' }} />
                <div style={{ fontSize: '52px', marginBottom: '8px', filter: 'drop-shadow(0 0 20px rgba(243,156,18,0.8))' }}>🏆</div>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#f39c12', textTransform: 'uppercase', letterSpacing: '2.5px', marginBottom: '8px' }}>Champion</div>
                <div style={{ fontSize: 'clamp(18px, 4vw, 26px)', fontWeight: '900', color: '#f39c12', lineHeight: 1.2, marginBottom: '6px', textShadow: '0 0 20px rgba(243,156,18,0.5)' }}>
                  {champion.name}
                </div>
                {champion.wins != null && (
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '6px' }}>
                    {champion.wins ?? 0}W · {champion.losses ?? 0}L
                  </div>
                )}
              </div>

              {/* Runner-up card */}
              {runnerUp && (
                <div style={{ background: 'linear-gradient(135deg,#0f1411 0%,#1a1f1a 40%,#1a1a2e 100%)', borderRadius: '18px', padding: '28px 20px', textAlign: 'center', position: 'relative', overflow: 'hidden', border: '2px solid rgba(180,180,200,0.25)', boxShadow: '0 0 24px rgba(180,180,200,0.06), inset 0 1px 0 rgba(255,255,255,0.04)' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 0%, rgba(192,192,220,0.1) 0%, transparent 65%)', pointerEvents: 'none' }} />
                  <div style={{ fontSize: '52px', marginBottom: '8px', filter: 'drop-shadow(0 0 12px rgba(192,192,220,0.4))' }}>🥈</div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#aab0c0', textTransform: 'uppercase', letterSpacing: '2.5px', marginBottom: '8px' }}>Runner-up</div>
                  <div style={{ fontSize: 'clamp(18px, 4vw, 26px)', fontWeight: '900', color: '#c8cdd8', lineHeight: 1.2, marginBottom: '6px' }}>
                    {runnerUp.name}
                  </div>
                  {runnerUp.wins != null && (
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginTop: '6px' }}>
                      {runnerUp.wins ?? 0}W · {runnerUp.losses ?? 0}L
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          tournament?.status !== 'completed' && (
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: '16px', padding: '32px', textAlign: 'center', marginBottom: '24px', color: 'rgba(255,255,255,0.3)', fontSize: '14px' }}>
              Tournament in progress — results will appear here once completed.
            </div>
          )
        )}

        {/* ── FINAL MATCH RESULT ── */}
        {finalMatch && (
          <div style={{ marginBottom: '28px' }}>
            <div style={{ textAlign: 'center', marginBottom: '14px' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#f39c12', textTransform: 'uppercase', letterSpacing: '2px', background: 'rgba(243,156,18,0.08)', border: '1px solid rgba(243,156,18,0.2)', borderRadius: '20px', padding: '4px 16px' }}>
                Grand Final Result
              </span>
            </div>
            <FinalMatchCard match={finalMatch} />
          </div>
        )}

        {/* ── STATS GRID ── */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '14px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#27ae60', textTransform: 'uppercase', letterSpacing: '2px', background: 'rgba(39,174,96,0.08)', border: '1px solid rgba(39,174,96,0.2)', borderRadius: '20px', padding: '4px 16px' }}>
              Tournament Statistics
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
            {stats.map((s, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '18px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: '26px', marginBottom: '8px' }}>{s.icon}</div>
                <div style={{ fontSize: 'clamp(16px, 3vw, 22px)', fontWeight: '900', color: '#fff', marginBottom: '4px', textTransform: 'capitalize' }}>{s.value}</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── MATCH RESULTS (most recent first) ── */}
        {(knockoutRounds.length > 0 || groupMatches.length > 0) && (
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: '800', color: '#fff', marginBottom: '16px' }}>Match Results</h2>

            {/* Knockout rounds: ascending roundNum = most recent stages first (SF4 before QF8) */}
            {knockoutRounds.map(rn => (
              <MatchResultGroup key={rn} title={getRoundLabel(rn)}
                matches={knockoutByRound[rn]}
                accent="#27ae60"
                descending />
            ))}

            {/* Group matches — most recently completed first */}
            {groupMatches.length > 0 && (
              <MatchResultGroup title="Group Stage" matches={groupMatches} accent="#2980b9" descending />
            )}
          </div>
        )}
      </div>

      <Footer dark />
    </div>
  );
}

/* ─── Grand Final card ─────────────────────────────────────────────────────── */
function FinalMatchCard({ match: m }) {
  const isT1Win = m.winner_team_id === m.team1_id;
  let sets = [];
  try { sets = JSON.parse(m.set_scores || '[]'); } catch {}
  const hasSets   = sets.length > 0;
  const t1Sets    = hasSets ? (m.team1_sets ?? sets.filter(s => Number(s.t1) > Number(s.t2)).length) : null;
  const t2Sets    = hasSets ? (m.team2_sets ?? sets.filter(s => Number(s.t2) > Number(s.t1)).length) : null;

  return (
    <div style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#16213e 60%,#0f3460 100%)', border: '1px solid rgba(243,156,18,0.3)', borderRadius: '16px', padding: '20px 24px', boxShadow: '0 4px 24px rgba(243,156,18,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Team 1 */}
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <div style={{ fontSize: '11px', color: isT1Win ? '#f39c12' : 'rgba(255,255,255,0.3)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            {isT1Win ? '🏆 Champion' : '🥈 Runner-up'}
          </div>
          <div style={{ fontSize: 'clamp(14px, 3vw, 18px)', fontWeight: '900', color: isT1Win ? '#fff' : 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {m.team1_name}
          </div>
        </div>

        {/* Score */}
        <div style={{ flexShrink: 0, textAlign: 'center', padding: '0 12px' }}>
          {hasSets ? (
            <div>
              {/* Sets tally — large */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '28px', fontWeight: '900', color: isT1Win ? '#f39c12' : 'rgba(255,255,255,0.5)' }}>{t1Sets}</span>
                <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.2)' }}>–</span>
                <span style={{ fontSize: '28px', fontWeight: '900', color: !isT1Win ? '#f39c12' : 'rgba(255,255,255,0.5)' }}>{t2Sets}</span>
              </div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px', fontWeight: '600' }}>SETS</div>
              {/* Set-by-set */}
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {sets.map((s, i) => {
                  const s1 = Number(s.t1), s2 = Number(s.t2);
                  const sw = s1 > s2 ? 1 : s2 > s1 ? 2 : 0;
                  return (
                    <span key={i} style={{ fontSize: '11px', background: 'rgba(255,255,255,0.05)', borderRadius: '5px', padding: '2px 6px', whiteSpace: 'nowrap' }}>
                      <span style={{ color: sw === 1 ? '#2ecc71' : '#e74c3c', fontWeight: '700' }}>{s.t1}</span>
                      <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 2px' }}>-</span>
                      <span style={{ color: sw === 2 ? '#2ecc71' : '#e74c3c', fontWeight: '700' }}>{s.t2}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '32px', fontWeight: '900', color: isT1Win ? '#f39c12' : 'rgba(255,255,255,0.5)' }}>{m.team1_score}</span>
              <span style={{ fontSize: '16px', color: 'rgba(255,255,255,0.2)' }}>–</span>
              <span style={{ fontSize: '32px', fontWeight: '900', color: !isT1Win ? '#f39c12' : 'rgba(255,255,255,0.5)' }}>{m.team2_score}</span>
            </div>
          )}
        </div>

        {/* Team 2 */}
        <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
          <div style={{ fontSize: '11px', color: !isT1Win ? '#f39c12' : 'rgba(255,255,255,0.3)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            {!isT1Win ? '🏆 Champion' : '🥈 Runner-up'}
          </div>
          <div style={{ fontSize: 'clamp(14px, 3vw, 18px)', fontWeight: '900', color: !isT1Win ? '#fff' : 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {m.team2_name}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Match result group ───────────────────────────────────────────────────── */
function MatchResultGroup({ title, matches, accent, descending }) {
  // Sort by completed_at descending (most recently completed first)
  const sorted = [...matches].sort((a, b) => {
    const ca = a.completed_at || '';
    const cb = b.completed_at || '';
    return descending ? cb.localeCompare(ca) : ca.localeCompare(cb);
  });

  return (
    <div style={{ marginBottom: '22px' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color: accent, textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ flex: 1, height: '1px', background: `${accent}44` }} />
        {title}
        <div style={{ flex: 1, height: '1px', background: `${accent}44` }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {sorted.map(m => {
          const isT1Win = m.winner_team_id === m.team1_id;
          let sets = [];
          try { sets = JSON.parse(m.set_scores || '[]'); } catch {}
          const hasSets = sets.length > 0;
          const t1Sets  = hasSets ? (m.team1_sets ?? sets.filter(s => Number(s.t1) > Number(s.t2)).length) : null;
          const t2Sets  = hasSets ? (m.team2_sets ?? sets.filter(s => Number(s.t2) > Number(s.t1)).length) : null;

          return (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: isT1Win ? '800' : '500', color: isT1Win ? '#2ecc71' : 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {isT1Win && '🏆 '}{m.team1_name}
                </div>
              </div>

              <div style={{ flexShrink: 0, textAlign: 'center', minWidth: hasSets ? '110px' : '56px' }}>
                {hasSets ? (
                  <div>
                    <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '2px' }}>
                      {sets.map((s, i) => {
                        const s1 = Number(s.t1), s2 = Number(s.t2);
                        const sw = s1 > s2 ? 1 : s2 > s1 ? 2 : 0;
                        return (
                          <span key={i} style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                            <span style={{ color: sw === 1 ? '#2ecc71' : '#e74c3c', fontWeight: '700' }}>{s.t1}</span>
                            <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 1px' }}>-</span>
                            <span style={{ color: sw === 2 ? '#2ecc71' : '#e74c3c', fontWeight: '700' }}>{s.t2}</span>
                            {i < sets.length - 1 && <span style={{ color: 'rgba(255,255,255,0.15)', marginLeft: '1px' }}>,</span>}
                          </span>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', fontWeight: '600' }}>
                      <span style={{ color: isT1Win ? '#2ecc71' : 'rgba(255,255,255,0.45)' }}>{t1Sets}</span>
                      <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 2px' }}>–</span>
                      <span style={{ color: !isT1Win && m.winner_team_id ? '#2ecc71' : 'rgba(255,255,255,0.45)' }}>{t2Sets}</span>
                      {' sets'}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: '900', color: isT1Win ? '#2ecc71' : 'rgba(255,255,255,0.65)' }}>{m.team1_score}</span>
                    <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '11px' }}>:</span>
                    <span style={{ fontSize: '15px', fontWeight: '900', color: !isT1Win && m.winner_team_id ? '#2ecc71' : 'rgba(255,255,255,0.65)' }}>{m.team2_score}</span>
                  </div>
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                <div style={{ fontSize: '13px', fontWeight: !isT1Win && m.winner_team_id ? '800' : '500', color: !isT1Win && m.winner_team_id ? '#2ecc71' : 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {!isT1Win && m.winner_team_id && '🏆 '}{m.team2_name}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
